/**
 * PATH: packages/email-hub/src/feature/components/email-allocation/useDueBucket.ts
 * FILE_ID: EMAILHUB-ALLOCATION-BUCKET-001-HOOK
 * PURPOSE: Owns due-bucket derivation, date formatting, and bucket mutations.
 */
import { useCallback } from 'react'

import { BUCKET_META, formatScheduledFor } from './helpers'
import type {
  AllocationBucket,
  DueBucketHookInput,
  DueBucketViewModel,
} from './types'

export function useDueBucket({
  emailId,
  assignment,
  busyKey,
  setBusyKey,
  setEmailAssignment,
  onAssignmentChanged,
  toast,
}: DueBucketHookInput): DueBucketViewModel {
  const currentBucket: AllocationBucket =
    (assignment?.bucket as AllocationBucket | undefined) ?? 'no_date'

  const pickBucket = useCallback(
    async (bucket: AllocationBucket) => {
      if (busyKey) return
      setBusyKey(`bucket:${bucket}`)
      try {
        const next = await setEmailAssignment(emailId, { bucket })
        onAssignmentChanged?.(next)
        toast(`Bucketed as ${BUCKET_META[bucket].label}`, 'success')
      } catch (err) {
        toast(
          `Could not update bucket: ${(err as Error).message || 'unknown error'}`,
          'error',
        )
      } finally {
        setBusyKey(null)
      }
    },
    [
      busyKey,
      emailId,
      setEmailAssignment,
      onAssignmentChanged,
      toast,
      setBusyKey,
    ],
  )

  return {
    busyKey,
    currentBucket,
    scheduledForLabel: formatScheduledFor(assignment?.scheduled_for),
    pickBucket,
  }
}
